import { useCallback, useEffect, useRef, useState, type FormEvent, type RefObject } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { useOnlineStatus } from "../hooks/useOnlineStatus";
import { mfaClient, recordMfaEnrollmentConfirmed, setMfaClientSession } from "../api/mfaAuthClient";
import { Button } from "../components/Button";
import { StatusBanner } from "../components/StatusBanner";
import { prepareMfaEnrollment } from "./mfaEnrollment";
// TextField (usado em ChangePasswordPage.tsx) não expõe ref para o <input> (não é forwardRef) — como
// esta tela precisa de foco automático programático no campo de código, o markup do campo é feito à mão
// aqui, reaproveitando as mesmas classes CSS (field/field__label/field__control/field__input/
// field__error) para manter a aparência idêntica ao resto do formulário do projeto.

const CODE_LENGTH = 6;

function qrCodeSrc(qrCode: string): string {
  return qrCode.startsWith("data:image/")
    ? qrCode
    : `data:image/svg+xml;utf-8,${encodeURIComponent(qrCode)}`;
}

type Phase = "enrolling" | "ready" | "verifying" | "audit-error" | "success" | "error-enroll";

/** true nas fases em que o QR/formulário deve ficar visível (fora para não somar ao complexity de MfaEnrollPage). */
function isFormPhase(phase: Phase): boolean {
  return phase === "ready" || phase === "verifying";
}

/**
 * accessToken/refreshToken só existem quando state.status === "authenticated" — extraído para não somar
 * ao complexity de MfaEnrollPage (mesmo motivo de isFormPhase). Os hooks abaixo (useCallback/useEffect)
 * precisam rodar em toda renderização, mesmo antes do narrowing de status (Rules of Hooks); string vazia
 * nunca é usada de verdade, porque ProtectedRoute só monta esta página com sessão autenticada.
 */
function sessionTokens(state: ReturnType<typeof useAuth>["state"]): { accessToken: string; refreshToken: string } {
  return state.status === "authenticated"
    ? { accessToken: state.accessToken, refreshToken: state.refreshToken }
    : { accessToken: "", refreshToken: "" };
}

type VerifiedSession = { accessToken: string; refreshToken: string; expiresIn?: number };

interface AuditErrorRetryProps {
  phase: Phase;
  verifiedSession: VerifiedSession | null;
  factorId: string | null;
  isOnline: boolean;
  onRetry: (factorId: string, session: VerifiedSession) => void;
}

/**
 * Extraído pelo mesmo motivo de MfaEnrollForm: a checagem de "pode mostrar o botão de repetir a
 * auditoria" fica isolada aqui, fora de MfaEnrollPage, para não somar ao complexity dela — chamado
 * incondicionalmente no JSX, sem `&&` no componente principal.
 */
function AuditErrorRetry({ phase, verifiedSession, factorId, isOnline, onRetry }: AuditErrorRetryProps) {
  if (phase !== "audit-error" || !verifiedSession || !factorId) return null;
  return (
    <Button type="button" onClick={() => void onRetry(factorId, verifiedSession)} disabled={!isOnline}>
      Tentar novamente
    </Button>
  );
}

interface MfaEnrollFormProps {
  qrSvg: string | null;
  secret: string | null;
  phase: Phase;
  code: string;
  setCode: (value: string) => void;
  fieldError: string | null;
  isOnline: boolean;
  codeInputRef: RefObject<HTMLInputElement>;
  onSubmit: (event: FormEvent) => void;
}

interface MfaEnrollViewProps {
  phase: Phase;
  error: string | null;
  isOnline: boolean;
  onStartEnrollment: () => void;
  factorId: string | null;
  qrSvg: string | null;
  secret: string | null;
  code: string;
  setCode: (value: string) => void;
  fieldError: string | null;
  codeInputRef: RefObject<HTMLInputElement>;
  onSubmit: (event: FormEvent) => void;
  verifiedSession: VerifiedSession | null;
  onRetryAudit: (factorId: string, session: VerifiedSession) => void;
}

interface MfaEnrollStatusProps {
  phase: Phase;
  error: string | null;
  isOnline: boolean;
  onStartEnrollment: () => void;
}

function MfaEnrollStatus({ phase, error, isOnline, onStartEnrollment }: MfaEnrollStatusProps) {
  return (
    <>
      {!isOnline && <StatusBanner tone="warning">Sem conexão com a internet no momento.</StatusBanner>}
      {error && <StatusBanner tone="error">{error}</StatusBanner>}
      {phase === "enrolling" && <p role="status">Preparando sua matrícula de autenticação…</p>}
      {phase === "error-enroll" && (
        <Button type="button" onClick={onStartEnrollment} disabled={!isOnline}>
          Tentar novamente
        </Button>
      )}
      {phase === "success" && <p role="status">Matrícula concluída. Redirecionando…</p>}
    </>
  );
}

interface MfaEnrollFieldsProps {
  phase: Phase;
  factorId: string | null;
  qrSvg: string | null;
  secret: string | null;
  code: string;
  setCode: (value: string) => void;
  fieldError: string | null;
  isOnline: boolean;
  codeInputRef: RefObject<HTMLInputElement>;
  onSubmit: (event: FormEvent) => void;
}

function MfaEnrollFields({ phase, factorId, qrSvg, secret, code, setCode, fieldError, isOnline, codeInputRef, onSubmit }: MfaEnrollFieldsProps) {
  if (!factorId || !isFormPhase(phase)) return null;
  return (
    <>
      {!qrSvg && !secret && <p>Seu aplicativo autenticador já está cadastrado. Informe o código atual para concluir a confirmação de segurança.</p>}
      <MfaEnrollForm
        qrSvg={qrSvg}
        secret={secret}
        phase={phase}
        code={code}
        setCode={setCode}
        fieldError={fieldError}
        isOnline={isOnline}
        codeInputRef={codeInputRef}
        onSubmit={onSubmit}
      />
    </>
  );
}

function MfaEnrollView({
  phase,
  error,
  isOnline,
  onStartEnrollment,
  factorId,
  qrSvg,
  secret,
  code,
  setCode,
  fieldError,
  codeInputRef,
  onSubmit,
  verifiedSession,
  onRetryAudit,
}: MfaEnrollViewProps) {
  return (
    <div className="centered-page">
      <div className="card">
        <h1>Matrícula obrigatória de autenticação em duas etapas</h1>
        <p className="card__subtitle">
          Sua função exige uma camada extra de segurança no acesso ao sistema. Escaneie o QR code abaixo
          com um aplicativo autenticador (Google Authenticator, Authy ou similar) e informe o código
          gerado para concluir. O código muda periodicamente — cada vez que você entrar, o aplicativo terá
          um novo. Isso só precisa ser feito uma vez por dispositivo.
        </p>

        <MfaEnrollStatus phase={phase} error={error} isOnline={isOnline} onStartEnrollment={onStartEnrollment} />
        <MfaEnrollFields
          phase={phase}
          factorId={factorId}
          qrSvg={qrSvg}
          secret={secret}
          code={code}
          setCode={setCode}
          fieldError={fieldError}
          isOnline={isOnline}
          codeInputRef={codeInputRef}
          onSubmit={onSubmit}
        />

        <AuditErrorRetry
          phase={phase}
          verifiedSession={verifiedSession}
          factorId={factorId}
          isOnline={isOnline}
          onRetry={onRetryAudit}
        />

      </div>
    </div>
  );
}

/**
 * Fase F1. Extraído de MfaEnrollPage só para manter a complexidade ciclomática do componente principal
 * dentro do limite do projeto (lint:complexity) — nenhuma mudança de comportamento ou de DOM final em
 * relação à versão anterior, tudo aqui é o mesmo bloco de QR code + formulário de código.
 */
function MfaEnrollForm({ qrSvg, secret, phase, code, setCode, fieldError, isOnline, codeInputRef, onSubmit }: MfaEnrollFormProps) {
  const busy = phase === "enrolling" || phase === "verifying";

  return (
    <>
      {qrSvg && secret && <img
        className="mfa-enroll__qr"
        // XSS: nunca usamos dangerouslySetInnerHTML com o SVG bruto devolvido pelo GoTrue. Ele vira
        // a fonte de uma <img> via data-URI — o navegador trata isso como imagem, nunca como HTML/
        // script executável no DOM da página, mesmo que o conteúdo do SVG venha adulterado.
        src={qrCodeSrc(qrSvg)}
        alt="QR code para configurar o aplicativo autenticador"
        width={200}
        height={200}
      />}
      {qrSvg && secret && <p>
        Não consegue escanear? Informe esta chave manualmente no aplicativo:{" "}
        <strong className="mfa-enroll__secret">{secret}</strong>
        <br />
        Trate esta chave como uma credencial: nunca a fotografe para compartilhar, nunca a envie por
        mensagem ou e-mail.
      </p>}

      <form onSubmit={onSubmit} noValidate>
        <div className="field">
          <label className="field__label" htmlFor="mfa-code">
            Código do aplicativo autenticador
          </label>
          <div className="field__control">
            <input
              ref={codeInputRef}
              id="mfa-code"
              className="field__input"
              name="mfa-code"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={CODE_LENGTH}
              aria-label="Código de 6 dígitos do aplicativo autenticador"
              aria-describedby={fieldError ? "mfa-code-error" : undefined}
              aria-invalid={Boolean(fieldError)}
              value={code}
              onChange={(event) => setCode(event.target.value.replace(/\D/g, "").slice(0, CODE_LENGTH))}
              disabled={busy}
            />
          </div>
          {fieldError && (
            <span className="field__error" id="mfa-code-error" role="alert">
              {fieldError}
            </span>
          )}
        </div>
        <Button type="submit" loading={phase === "verifying"} disabled={!isOnline || code.length !== CODE_LENGTH}>
          Confirmar matrícula
        </Button>
      </form>
    </>
  );
}

/**
 * Fase F1 (ADR-054). Mesmo padrão estrutural de ChangePasswordPage.tsx (layout centered-page/card,
 * banner offline, 401 real via expireSession()). Matrícula em si fala direto com o GoTrue via
 * mfaClient (api/mfaAuthClient.ts) — nenhuma Edge Function nova, nenhum dado de matrícula (QR/segredo)
 * é persistido fora do estado deste componente: nem em StoredSession, nem em localStorage/
 * sessionStorage.
 *
 * @F1-ENROLL-003: se o usuário sair sem confirmar, o fator fica não-verified no GoTrue. Antes de criar
 * um novo fator, a tela remove somente esses fatores TOTP órfãos sem nome amigável — nunca um fator
 * verified — e então inicia uma matrícula nova com QR/segredo novos.
 */
export function MfaEnrollPage() {
  const { state, expireSession, applyMfaVerifiedSession } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isOnline = useOnlineStatus();

  const [phase, setPhase] = useState<Phase>("enrolling");
  const [factorId, setFactorId] = useState<string | null>(null);
  const [qrSvg, setQrSvg] = useState<string | null>(null);
  const [secret, setSecret] = useState<string | null>(null);
  const [code, setCode] = useState("");
  // `error`: falhas de rede/servidor/enroll — banner de topo (StatusBanner, role="status", mesmo padrão
  // de ChangePasswordPage.tsx). `fieldError`: erro específico do código informado (formato ou código
  // TOTP incorreto) — anunciado junto ao campo via role="alert", sem duplicar o banner de topo.
  const [error, setError] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<string | null>(null);
  // Tokens novos (AAL2) devolvidos por verify() — guardados aqui só entre a confirmação do fator e a
  // gravação bem-sucedida da auditoria, para permitir "tentar novamente" a RPC sem repetir verify().
  const [verifiedSession, setVerifiedSession] = useState<VerifiedSession | null>(null);

  const codeInputRef = useRef<HTMLInputElement>(null);

  // Achado do Codex (26/08/2026, violação das Rules of Hooks): o early-return por status ficava ANTES
  // dos hooks abaixo (useCallback/useEffect), fazendo o React executar uma quantidade diferente de hooks
  // entre uma renderização autenticada e uma não-autenticada (por exemplo, logo após expireSession() no
  // meio de um enroll/challenge/verify) — isso pode derrubar a tela com erro do React. Todo hook agora
  // roda em toda renderização; a guarda de status vira um return comum, depois de todos eles.
  const { accessToken, refreshToken } = sessionTokens(state);

  const startEnrollment = useCallback(async () => {
    setPhase("enrolling");
    setError(null);
    setFactorId(null);
    setQrSvg(null);
    setSecret(null);
    setCode("");

    // Recomendação do Codex (26/08/2026): ProtectedRoute só monta esta página com sessão autenticada, mas
    // uma montagem transitória (ex.: expireSession() disparado bem no meio da troca de renderizações)
    // poderia, em teoria, deixar accessToken/refreshToken vazios (sessionTokens() acima). Sem esta guarda,
    // setMfaClientSession seria chamado com tokens vazios — mais defensivo simplesmente não tentar.
    if (!accessToken || !refreshToken) return;

    try {
      // O mfaClient não persiste sessão própria (ver comentário em api/mfaAuthClient.ts) — cada
      // tentativa de matrícula "empresta" o token já validado por auth-login antes de falar com o
      // GoTrue.
      await setMfaClientSession(accessToken, refreshToken);
      const preparation = await prepareMfaEnrollment(mfaClient.auth.mfa);
      if (preparation.kind === "unauthorized") {
        expireSession();
        return;
      }
      if (preparation.kind === "error") throw preparation.error;

      if (preparation.kind === "reuse-verified") {
        // Uma auditoria interrompida pode deixar o GoTrue com fator verified, mas sem o evento local
        // correspondente. Nesse estado, criar outro fator causa mfa_factor_name_conflict; reutilizar o
        // fator válido permite repetir challenge/verify e concluir a auditoria sem apagar MFA existente.
        setFactorId(preparation.factorId);
        setError(null);
        setFieldError(null);
        setPhase("ready");
        return;
      }

      setFactorId(preparation.factorId);
      setQrSvg(preparation.qrCode);
      setSecret(preparation.secret);
      setError(null);
      setFieldError(null);
      setPhase("ready");
    } catch {
      setError("Não foi possível iniciar a matrícula de autenticação agora. Tente novamente.");
      setPhase("error-enroll");
    }
  }, [accessToken, refreshToken, expireSession]);

  useEffect(() => {
    void startEnrollment();
    // Roda uma vez por montagem — @F1-ENROLL-003: se o usuário sair e voltar (novo mount), isto roda
    // de novo e emite um QR/factorId novos. Deps vazias são intencionais: startEnrollment já depende
    // de accessToken/refreshToken/expireSession (ver useCallback acima), e reagir a esses valores aqui
    // reiniciaria a matrícula sempre que a sessão fosse renovada.
  }, []);

  useEffect(() => {
    if (phase === "ready") {
      codeInputRef.current?.focus();
    }
  }, [phase]);

  /**
   * Achado do Codex (26/08/2026): antes, uma falha na RPC de auditoria era só logada e o usuário mesmo
   * assim era liberado (best-effort) — quebra a promessa de rastreabilidade da matrícula. Agora a tela
   * fica bloqueada em "audit-error" com um botão de repetir SÓ a chamada da RPC (o fator já está verified
   * no GoTrue, então não é preciso pedir um novo código) até a auditoria ser gravada com sucesso; só então
   * a sessão é promovida a AAL2 e a navegação acontece.
  */
  async function confirmAuditAndRelease(currentFactorId: string, session: VerifiedSession) {
    let rpcError: unknown = null;
    try {
      // O cliente REST do supabase-js compartilha a sessão do cliente Auth, mas o verify() acabou de
      // trocar o JWT. Reaplicar explicitamente o par novo evita que a RPC seja enviada como anon/apikey
      // em uma corrida entre o estado interno do GoTrue e o PostgREST.
      await setMfaClientSession(session.accessToken, session.refreshToken);
      ({ error: rpcError } = await recordMfaEnrollmentConfirmed(currentFactorId, session.accessToken));
    } catch (error) {
      rpcError = error;
    }
    if (rpcError) {
      console.error("MfaEnrollPage: falha ao registrar auditoria de matrícula confirmada", rpcError);
      setError("A matrícula foi confirmada no aplicativo autenticador, mas não foi possível registrar a auditoria agora. Tente novamente.");
      setPhase("audit-error");
      return;
    }

    // Achado do Codex (26/08/2026): verify() já tinha promovido a sessão do GoTrue para AAL2, mas o
    // AuthContext continuava com o JWT antigo (AAL1) — só agora, com a auditoria gravada, o resto do app
    // passa a usar os tokens novos.
    applyMfaVerifiedSession(session);
    setVerifiedSession(null);
    setError(null);
    setPhase("success");
    // Achado do auditor-frontend (25/08/2026): navigate() imediatamente após setPhase("success") sem
    // nenhum intervalo desmonta o componente no mesmo ciclo, antes de um leitor de tela ter chance de
    // anunciar o role="status" de sucesso abaixo. O atraso dá tempo real para o anúncio ser lido sem
    // ficar perceptível como demora para quem usa mouse/teclado.
    const retorno = searchParams.get("retorno");
    window.setTimeout(() => {
      navigate(retorno && retorno.startsWith("/") ? retorno : "/dashboard", { replace: true });
    }, 800);
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (!factorId) return;
    if (code.length !== CODE_LENGTH) {
      setFieldError(`Informe os ${CODE_LENGTH} dígitos do código.`);
      return;
    }

    setError(null);
    setFieldError(null);
    setPhase("verifying");
    try {
      const { data: challenge, error: challengeError } = await mfaClient.auth.mfa.challenge({ factorId });
      if (challengeError) {
        if (challengeError.status === 401) {
          expireSession();
          return;
        }
        // Erro de rede/servidor ao gerar o desafio — não é código errado. Mesmo factorId, sem novo QR.
        setError("Não foi possível validar o código agora. Tente novamente.");
        setPhase("ready");
        return;
      }

      const { data: verifyData, error: verifyError } = await mfaClient.auth.mfa.verify({
        factorId,
        challengeId: challenge.id,
        code,
      });
      if (verifyError) {
        if (verifyError.status === 401) {
          expireSession();
          return;
        }
        // @F1-ENROLL-002: código inválido é erro de negócio do verify(), não expiração de sessão — a
        // RPC de auditoria NUNCA é chamada aqui, e a tela permite nova tentativa sem reemitir QR novo.
        setFieldError("Código inválido. Confira o aplicativo autenticador e tente novamente.");
        setCode("");
        setPhase("ready");
        return;
      }

      // @F1-ENROLL-004: a RPC de auditoria só é chamada DEPOIS do verify() confirmar sucesso — nunca
      // otimisticamente antes disso (a chamada acima já retornou sem erro neste ponto).
      const newSession = { accessToken: verifyData.access_token, refreshToken: verifyData.refresh_token, expiresIn: verifyData.expires_in };
      setVerifiedSession(newSession);
      await confirmAuditAndRelease(factorId, newSession);
    } catch {
      setError("Não foi possível validar o código agora. Tente novamente.");
      setPhase("ready");
    }
  }

  if (state.status !== "authenticated") {
    // ProtectedRoute já garante isso — a guarda fica DEPOIS de todos os hooks acima (Rules of Hooks,
    // achado do Codex), servindo só para o TypeScript fazer o narrowing do restante do JSX abaixo.
    return null;
  }

  return <MfaEnrollView phase={phase} error={error} isOnline={isOnline} onStartEnrollment={() => void startEnrollment()} factorId={factorId} qrSvg={qrSvg} secret={secret} code={code} setCode={setCode} fieldError={fieldError} codeInputRef={codeInputRef} onSubmit={handleSubmit} verifiedSession={verifiedSession} onRetryAudit={confirmAuditAndRelease} />;
}
